// YDG WRLD JEWELS — Product Detail Page (PDP)
// Vanilla JS, no frameworks, no build step.
// Data source: YDG_PRODUCTS (loaded via js/products.js before this script).

(function () {
  'use strict';

  // ================================================================
  //  CONSTANTS & HELPERS
  // ================================================================

  const CART_KEY = 'ydg_cart';

  const CATEGORY_COPY = {
    cross:     "It's been everywhere. This one's yours next.",
    necklaces: "Hand-set. Finite runs. Wear it every day, everywhere.",
    bracelets: "Stacked or solo. Every piece is a limited run · no restocks."
  };

  // Color name -> approximate hex for swatch dots
  const COLOR_HEX = {
    blue:   '#5fb7d4',
    red:    '#c8452e',
    green:  '#4a8c62',
    pink:   '#e58fb1',
    yellow: '#e8c34a',
    gold:   '#c9a227',
    silver: '#b0b4bc',
    aqua:   '#59e0d8',
    ruby:   '#c8452e',
    white:  '#f5f0e6',
    black:  '#1a1a1a'
  };

  function getColorHex(name) {
    if (!name) return '#999';
    const lc = name.toLowerCase();
    for (const key of Object.keys(COLOR_HEX)) {
      if (lc.includes(key)) return COLOR_HEX[key];
    }
    return '#c0bbb4';
  }

  function fmtPrice(p, soldOut) {
    const n = parseFloat(p);
    if (soldOut) return 'SOLD OUT';
    return '$' + (isNaN(n) ? 0 : n).toFixed(2);
  }

  function escHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ================================================================
  //  FLATTEN ALL PRODUCTS
  // ================================================================

  // YDG_PRODUCTS is keyed by category. Each product gets a .category field.
  const ALL_PRODUCTS = [];
  const CATEGORY_MAP = {};  // handle -> category key

  if (typeof YDG_PRODUCTS !== 'undefined') {
    for (const cat of Object.keys(YDG_PRODUCTS)) {
      for (const p of YDG_PRODUCTS[cat]) {
        const prod = Object.assign({ category: cat }, p);
        // WRLD CROSS pendants are necklaces at heart — merge into the
        // "necklaces" category for browsing/breadcrumb purposes, but
        // remember the original so swatch-by-cross-colorway logic still
        // works (isCross flag).
        prod.isCross = (cat === 'cross');
        if (cat === 'cross') prod.category = 'necklaces';
        ALL_PRODUCTS.push(prod);
        CATEGORY_MAP[p.handle] = prod.category;
      }
    }
  }

  // ================================================================
  //  CART LOGIC (real Shopify Storefront API cart, via window.YDGCart)
  // ================================================================

  // Shopify cart lines don't carry our local `handle` — they carry the
  // Shopify variant/line info directly. Helpers below read from the
  // live YDGCart cache instead of localStorage.

  function shopifyCartLines() {
    const cart = window.YDGCart && window.YDGCart.getCached();
    if (!cart || !cart.lines || !cart.lines.edges) return [];
    return cart.lines.edges.map(function (e) { return e.node; });
  }

  function cartCount() {
    const cart = window.YDGCart && window.YDGCart.getCached();
    return cart ? (cart.totalQuantity || 0) : 0;
  }

  function cartAdd(variantId, title, price, image, qty) {
    if (!window.YDGCart) return Promise.resolve();
    return window.YDGCart.add(variantId, qty).then(function () {
      updateCartBadge();
    });
  }

  function cartRemove(lineId) {
    if (!window.YDGCart) return;
    window.YDGCart.removeLine(lineId).then(function () {
      updateCartBadge();
      renderCartItems();
    });
  }

  function cartUpdateQty(lineId, delta, currentQty) {
    if (!window.YDGCart) return;
    const newQty = Math.max(1, (currentQty || 1) + delta);
    window.YDGCart.updateLine(lineId, newQty).then(function () {
      updateCartBadge();
      renderCartItems();
    });
  }

  function cartSubtotal() {
    const cart = window.YDGCart && window.YDGCart.getCached();
    if (!cart || !cart.cost) return 0;
    return parseFloat(cart.cost.subtotalAmount.amount) || 0;
  }

  function updateCartBadge() {
    const count = cartCount();
    const el = document.getElementById('pdpCartCount');
    if (el) {
      el.textContent = count;
      el.classList.toggle('is-zero', count === 0);
    }
  }

  // ================================================================
  //  SPECS INFERENCE
  // ================================================================

  function inferSpecs(product) {
    const t = product.title.toUpperCase();
    const specs = [];

    // Metal
    if (t.includes('GOLD')) specs.push({ label: 'METAL', val: 'GOLD PLATED' });
    else if (t.includes('SILVER')) specs.push({ label: 'METAL', val: 'SILVER PLATED' });
    else specs.push({ label: 'METAL', val: 'MIXED METALS' });

    // Stone / color
    if (t.includes('BLUE'))   specs.push({ label: 'STONE', val: 'BLUE TOPAZ' });
    else if (t.includes('RED'))    specs.push({ label: 'STONE', val: 'RUBY' });
    else if (t.includes('GREEN'))  specs.push({ label: 'STONE', val: 'EMERALD' });
    else if (t.includes('PINK'))   specs.push({ label: 'STONE', val: 'ROSE QUARTZ' });
    else if (t.includes('YELLOW')) specs.push({ label: 'STONE', val: 'CITRINE' });
    else if (t.includes('CROSS'))  specs.push({ label: 'STONE', val: 'BAGUETTE CUT' });

    // Chain length (crosses + necklaces)
    if (product.isCross) specs.push({ label: 'CHAIN', val: '24 IN · ADJUSTABLE' });
    else if (product.category === 'necklaces') specs.push({ label: 'CHAIN', val: '18 IN · ADJUSTABLE' });

    // Run
    specs.push({ label: 'RUN', val: 'LIMITED RUN · NO RESTOCKS' });

    return specs;
  }

  // ================================================================
  //  VARIANT GROUPING (colorway swatches)
  //  Group crosses by GOLD/SILVER base + cross pattern.
  //  e.g. "WRLD CROSS GOLD - Blue" groups with "WRLD CROSS GOLD - Red"
  // ================================================================

  function getVariantGroup(product) {
    if (!product.isCross) return null;
    // Determine base: GOLD or SILVER
    const t = product.title.toUpperCase();
    let base = null;
    if (t.includes('GOLD')) base = 'GOLD';
    else if (t.includes('SILVER')) base = 'SILVER';
    if (!base) return null;

    // Find all others in same cross group with same base
    const variants = ALL_PRODUCTS.filter(function (p) {
      if (!p.isCross) return false;
      const pt = p.title.toUpperCase();
      return pt.includes(base);
    });

    // Extract color label from title (last word after '-')
    return variants.map(function (p) {
      const parts = p.title.split('-');
      const colorName = parts.length > 1 ? parts[parts.length - 1].trim() : p.title;
      return {
        handle: p.handle,
        colorName: colorName,
        hex: getColorHex(colorName),
        isActive: p.handle === product.handle
      };
    });
  }

  // ================================================================
  //  GALLERY
  // ================================================================

  let _galleryImages = [];
  let _activeThumb = 0;
  let _titleForAlt = '';

  function initGallery(images, titleForAlt) {
    _galleryImages = images || [];
    _activeThumb = 0;
    _titleForAlt = titleForAlt || '';

    const mainImg = document.getElementById('pdpMainImg');
    const thumbsEl = document.getElementById('pdpThumbs');
    const sideEl = document.getElementById('pdpMosaicSide');
    const captionEl = document.getElementById('pdpMosaicCaption');
    if (!mainImg || !thumbsEl) return;

    if (_galleryImages.length === 0) return;

    mainImg.src = _galleryImages[0];
    mainImg.alt = _titleForAlt;
    if (captionEl) captionEl.textContent = _titleForAlt;

    // Side mosaic: up to 2 small stacked shots beside the main image
    // (editorial magazine-grid look, not a single flat hero).
    if (sideEl) {
      const sideShots = _galleryImages.slice(1, 3);
      sideEl.innerHTML = sideShots.map(function (src, i) {
        return '<img class="pdp-mosaic-side-img" src="' + escHtml(src) + '" alt="' + escHtml(_titleForAlt) + ' detail ' + (i + 1) + '" loading="lazy">';
      }).join('');
      sideEl.style.display = sideShots.length ? '' : 'none';
    }

    thumbsEl.innerHTML = '';
    _galleryImages.forEach(function (src, i) {
      const img = document.createElement('img');
      img.className = 'pdp-thumb' + (i === 0 ? ' is-active' : '');
      img.src = src;
      img.alt = 'View ' + (i + 1);
      img.loading = 'lazy';
      img.addEventListener('click', function () { switchImage(i); });
      thumbsEl.appendChild(img);
    });
  }

  function switchImage(idx) {
    const mainImg = document.getElementById('pdpMainImg');
    const thumbs = document.querySelectorAll('.pdp-thumb');
    if (!mainImg) return;
    if (idx < 0) idx = _galleryImages.length - 1;
    if (idx >= _galleryImages.length) idx = 0;

    mainImg.classList.add('is-fading');
    setTimeout(function () {
      mainImg.src = _galleryImages[idx];
      mainImg.classList.remove('is-fading');
    }, 150);

    thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
    _activeThumb = idx;
  }

  // ================================================================
  //  RENDER CART ITEMS
  // ================================================================

  function renderCartItems() {
    const container = document.getElementById('pdpCartItems');
    const subtotalEl = document.getElementById('pdpCartSubtotal');
    if (!container) return;

    const lines = shopifyCartLines();
    if (lines.length === 0) {
      container.innerHTML = '<p class="pdp-cart-empty">YOUR BAG IS EMPTY.</p>';
    } else {
      container.innerHTML = lines.map(function (line) {
        const m = line.merchandise;
        const title = (m.product && m.product.title) || m.title || 'Item';
        const img = (m.image && m.image.url) || '';
        const lineTotal = parseFloat(line.cost.totalAmount.amount).toFixed(2);
        return '<div class="pdp-cart-item" data-line-id="' + escHtml(line.id) + '">' +
          '<img class="pdp-cart-item-img" src="' + escHtml(img) + '" alt="' + escHtml(title) + '" loading="lazy">' +
          '<div>' +
            '<p class="pdp-cart-item-title">' + escHtml(title) + '</p>' +
            '<p class="pdp-cart-item-price">$' + lineTotal + '</p>' +
            '<div class="pdp-cart-item-qty">' +
              '<button class="pdp-cart-item-qty-btn" data-action="dec" data-line-id="' + escHtml(line.id) + '" data-qty="' + line.quantity + '" aria-label="Decrease">&#8722;</button>' +
              '<span class="pdp-cart-item-qty-val">' + line.quantity + '</span>' +
              '<button class="pdp-cart-item-qty-btn" data-action="inc" data-line-id="' + escHtml(line.id) + '" data-qty="' + line.quantity + '" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>' +
          '<button class="pdp-cart-item-remove" data-line-id="' + escHtml(line.id) + '" aria-label="Remove">&#215;</button>' +
        '</div>';
      }).join('');

      // Wire qty/remove listeners
      container.querySelectorAll('.pdp-cart-item-qty-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const lineId = btn.dataset.lineId;
          const qty = parseInt(btn.dataset.qty, 10) || 1;
          const delta = btn.dataset.action === 'inc' ? 1 : -1;
          cartUpdateQty(lineId, delta, qty);
        });
      });
      container.querySelectorAll('.pdp-cart-item-remove').forEach(function (btn) {
        btn.addEventListener('click', function () {
          cartRemove(btn.dataset.lineId);
        });
      });
    }

    if (subtotalEl) {
      subtotalEl.textContent = '$' + cartSubtotal().toFixed(2);
    }

    // Checkout button — hand off to real Shopify-hosted checkout.
    const checkoutBtn = document.getElementById('pdpCartCheckout');
    if (checkoutBtn) {
      const url = window.YDGCart && window.YDGCart.getCheckoutUrl();
      if (url) {
        checkoutBtn.href = url;
        checkoutBtn.removeAttribute('target');
        checkoutBtn.classList.remove('is-disabled');
      } else {
        checkoutBtn.href = '#';
        checkoutBtn.classList.add('is-disabled');
      }
    }
  }

  // ================================================================
  //  CART DRAWER OPEN/CLOSE
  // ================================================================

  function openCartDrawer() {
    const overlay = document.getElementById('pdpCartOverlay');
    const drawer  = document.getElementById('pdpCartDrawer');
    if (!overlay || !drawer) return;
    renderCartItems();
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    const overlay = document.getElementById('pdpCartOverlay');
    const drawer  = document.getElementById('pdpCartDrawer');
    if (!overlay || !drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // ================================================================
  //  RENDER PRODUCT PAGE
  // ================================================================

  function renderProduct(product) {
    document.title = product.title + ' · YDG WRLD JEWELS';

    // Bracketed slab headline
    const slabText = document.getElementById('pdpSlabText');
    if (slabText) slabText.textContent = product.title.toUpperCase();

    // Breadcrumb
    const catLabel = product.category.charAt(0).toUpperCase() + product.category.slice(1);
    const breadcrumb = document.getElementById('pdpBreadcrumb');
    if (breadcrumb) {
      breadcrumb.innerHTML =
        '<a href="/">HOME</a>' +
        '<span>/</span>' +
        '<a href="/pages/shop?cat=' + escHtml(product.category) + '">' + escHtml(catLabel.toUpperCase()) + '</a>' +
        '<span>/</span>' +
        '<span>' + escHtml(product.title) + '</span>';
    }

    // Category tag
    const tagEl = document.getElementById('pdpCategoryTag');
    if (tagEl) tagEl.textContent = catLabel.toUpperCase();

    // Title
    const titleEl = document.getElementById('pdpTitle');
    if (titleEl) titleEl.textContent = product.title;

    // Price
    const priceEl = document.getElementById('pdpPrice');
    if (priceEl) {
      const fp = parseFloat(product.price);
      if (product.available === false) {
        priceEl.textContent = 'SOLD OUT';
      } else {
        priceEl.textContent = '$' + (isNaN(fp) ? 0 : fp).toFixed(2);
      }
    }

    // Tagline from category
    const taglineEl = document.getElementById('pdpTagline');
    if (taglineEl) taglineEl.textContent = CATEGORY_COPY[product.category] || CATEGORY_COPY.cross;

    // Swatches
    const swatchSection = document.getElementById('pdpSwatchSection');
    const swatchContainer = document.getElementById('pdpSwatches');
    const variantGroup = getVariantGroup(product);
    if (variantGroup && variantGroup.length > 1 && swatchSection && swatchContainer) {
      swatchSection.style.display = '';
      swatchContainer.innerHTML = variantGroup.map(function (v) {
        return '<a class="pdp-swatch-link' + (v.isActive ? ' is-active' : '') + '" href="/pages/product-1?handle=' + encodeURIComponent(v.handle) + '" title="' + escHtml(v.colorName) + '" aria-label="' + escHtml(v.colorName) + '">' +
          '<span class="pdp-swatch-dot" style="background:' + escHtml(v.hex) + '"></span>' +
          '<span class="pdp-swatch-label">' + escHtml(v.colorName.toUpperCase()) + '</span>' +
          '</a>';
      }).join('');
    } else if (swatchSection) {
      swatchSection.style.display = 'none';
    }

    // Specs
    const specsEl = document.getElementById('pdpSpecs');
    const specs = inferSpecs(product);
    if (specsEl) {
      specsEl.innerHTML = specs.map(function (s) {
        return '<div class="pdp-spec-chip">' +
          '<span class="pdp-spec-chip-label">' + escHtml(s.label) + '</span>' +
          '<span class="pdp-spec-chip-val">' + escHtml(s.val) + '</span>' +
          '</div>';
      }).join('');
    }

    // Top filter row (editorial reference style: "color / size / material / price")
    // repurposed here as a live display of this piece's actual attributes.
    const byLabel = {};
    specs.forEach(function (s) { byLabel[s.label] = s.val; });
    const fMetal = document.getElementById('pdpFilterMetal');
    const fStone = document.getElementById('pdpFilterStone');
    const fChain = document.getElementById('pdpFilterChain');
    const fPrice = document.getElementById('pdpFilterPrice');
    if (fMetal) fMetal.innerHTML = (byLabel.METAL || 'metal').toLowerCase() + ' <i>&#9662;</i>';
    if (fStone) fStone.innerHTML = (byLabel.STONE || 'stone').toLowerCase() + ' <i>&#9662;</i>';
    if (fChain) fChain.innerHTML = (byLabel.CHAIN || 'one size').toLowerCase() + ' <i>&#9662;</i>';
    if (fPrice) fPrice.innerHTML = fmtPrice(product.price, product.available === false).toLowerCase() + ' <i>&#9662;</i>';

    // Gallery
    initGallery(product.images, product.title);

    // Filmstrip arrows
    const fsPrev = document.getElementById('pdpFsPrev');
    const fsNext = document.getElementById('pdpFsNext');
    if (fsPrev) fsPrev.onclick = function () { switchImage(_activeThumb - 1); };
    if (fsNext) fsNext.onclick = function () { switchImage(_activeThumb + 1); };

    // "Also in the collection" — up to 4 from same category, skip current
    const alsoEl = document.getElementById('pdpAlsoGrid');
    if (alsoEl) {
      const siblings = ALL_PRODUCTS
        .filter(function (p) { return p.category === product.category && p.handle !== product.handle; })
        .slice(0, 4);

      if (siblings.length === 0) {
        const alsoSection = document.getElementById('pdpAlsoSection');
        if (alsoSection) alsoSection.style.display = 'none';
      } else {
        alsoEl.innerHTML = siblings.map(function (p) {
          const img = p.images && p.images[0] ? p.images[0] : '';
          return '<a class="pdp-card" href="/pages/product-1?handle=' + encodeURIComponent(p.handle) + '">' +
            '<div class="pdp-card-img-wrap">' +
              (img ? '<img class="pdp-card-img" src="' + escHtml(img) + '" alt="' + escHtml(p.title) + '" loading="lazy">' : '') +
            '</div>' +
            '<p class="pdp-card-title">' + escHtml(p.title) + '</p>' +
            '<p class="pdp-card-price">' + fmtPrice(p.price, p.available === false) + '</p>' +
            '</a>';
        }).join('');
      }
    }

    // ADD TO BAG button
    const addBtn = document.getElementById('pdpAddBtn');
    if (addBtn) {
      if (product.available === false) {
        addBtn.innerHTML = 'SOLD OUT';
        addBtn.disabled = true;
      } else if (!product.variantId) {
        console.error('[YDG] Product missing Shopify variantId, cannot add to cart:', product.handle);
        addBtn.disabled = true;
      } else {
        addBtn.addEventListener('click', function () {
          const qtyEl = document.getElementById('pdpQtyVal');
          const qty = parseInt((qtyEl && qtyEl.textContent) || '1', 10);
          addBtn.disabled = true;
          const originalText = addBtn.innerHTML;
          addBtn.innerHTML = 'ADDING&hellip;';
          cartAdd(product.variantId, product.title, product.price, null, qty)
            .then(function () {
              addBtn.disabled = false;
              addBtn.innerHTML = originalText;
              openCartDrawer();
            })
            .catch(function (err) {
              console.error('[YDG] Add to cart failed:', err);
              addBtn.disabled = false;
              addBtn.innerHTML = originalText;
              alert('Something went wrong adding this to your bag. Please try again.');
            });
        });
      }
    }
  }

  // ================================================================
  //  NOT FOUND STATE
  // ================================================================

  function renderNotFound(handle) {
    document.title = 'Piece Not Found · YDG WRLD JEWELS';
    const main = document.getElementById('pdpMain');
    if (!main) return;
    main.innerHTML =
      '<div class="pdp-not-found">' +
        '<h1>NOT FOUND.</h1>' +
        '<p>We couldn\'t find "' + escHtml(handle || 'that piece') + '".' +
        ' It may be sold out or the link might be off.</p>' +
        '<a href="/pages/shop">SHOP THE COLLECTION</a>' +
      '</div>';
  }

  // ================================================================
  //  NAV INIT (PDP nav)
  // ================================================================

  function initNav() {
    const nav = document.getElementById('pdpNav');
    const menuBtn = document.getElementById('pdpMenuBtn');
    const menu    = document.getElementById('pdpNavMenu');
    const cartBtn = document.getElementById('pdpCartBtn');

    if (nav) {
      window.addEventListener('scroll', function () {
        nav.classList.toggle('is-scrolled', window.scrollY > 20);
      }, { passive: true });
    }

    if (menuBtn && menu) {
      menuBtn.addEventListener('click', function () {
        const open = menu.classList.toggle('is-open');
        menuBtn.classList.toggle('is-open', open);
        menuBtn.setAttribute('aria-expanded', String(open));
      });
      menu.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function () {
          menu.classList.remove('is-open');
          menuBtn.classList.remove('is-open');
          menuBtn.setAttribute('aria-expanded', 'false');
        });
      });
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openCartDrawer();
      });
    }

    updateCartBadge();
  }

  // ================================================================
  //  INIT
  // ================================================================

  function init() {
    // Mark html as pdp-root (forces light bg override of style.css dark defaults)
    document.documentElement.classList.add('pdp-root');

    // Keep badge + drawer in sync as the real Shopify cart changes
    // (e.g. after add/remove/qty mutations resolve).
    if (window.YDGCart) {
      window.YDGCart.onChange(function () {
        updateCartBadge();
      });
    }

    initNav();

    // Wire cart close
    const cartClose = document.getElementById('pdpCartClose');
    const cartOverlay = document.getElementById('pdpCartOverlay');
    if (cartClose) cartClose.addEventListener('click', closeCartDrawer);
    if (cartOverlay) cartOverlay.addEventListener('click', function (e) {
      if (e.target === cartOverlay) closeCartDrawer();
    });

    // Qty stepper on PDP
    const qtyDec = document.getElementById('pdpQtyDec');
    const qtyInc = document.getElementById('pdpQtyInc');
    const qtyVal = document.getElementById('pdpQtyVal');
    if (qtyDec && qtyInc && qtyVal) {
      qtyDec.addEventListener('click', function () {
        const v = parseInt(qtyVal.textContent, 10);
        if (v > 1) qtyVal.textContent = v - 1;
      });
      qtyInc.addEventListener('click', function () {
        const v = parseInt(qtyVal.textContent, 10);
        qtyVal.textContent = v + 1;
      });
    }

    // Read handle from query string
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('handle');

    if (!handle) {
      renderNotFound('');
      return;
    }

    const product = ALL_PRODUCTS.find(function (p) { return p.handle === handle; });
    if (!product) {
      renderNotFound(handle);
      return;
    }

    renderProduct(product);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
